import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Textarea,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@stayafrica/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Ban,
  Banknote,
  Download,
  History,
  Mail,
  Paperclip,
  Receipt as ReceiptIcon,
  RotateCcw,
  Save,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auditQueryOptions } from '@/features/audit/queries';
import { useSession } from '@/features/auth/session';
import { billingKeys } from '@/features/billing/queries';
import { SendDocumentDialog } from '@/features/billing/SendDocumentDialog';
import { RefundDrawer } from '@/features/payments/RefundDrawer';
import {
  cashKeys,
  generateReceipt,
  issueThenDownloadReceipt,
  paymentKeys,
  paymentQueryOptions,
  updateAllocations,
  voidPayment,
} from '@/features/payments/queries';
import { methodTone, paymentActions, paymentBadge, refundableOf } from '@/features/payments/status';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/payments/$paymentId')({
  component: PaymentDetailPage,
});

function parseAmount(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? Number(digits) : 0;
}

/**
 * Fiche paiement (SCR-091) : détail, affectations éditables, reçu, annulation
 * et remboursement. Un encaissement n'est jamais supprimé — l'annulation
 * produit une écriture compensatoire tracée (R-006).
 */
function PaymentDetailPage() {
  const { paymentId } = Route.useParams();
  const { t, i18n } = useTranslation(['payments', 'cash', 'billing', 'common', 'errors', 'audit']);
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = session.can('payment.read');

  const query = useQuery(paymentQueryOptions(paymentId, canRead));
  const payment = query.data;

  const auditQuery = useInfiniteQuery({
    ...auditQueryOptions({ entityType: 'payment', entityId: paymentId }, 20),
    enabled: canRead && session.can('audit.read'),
  });

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidConfirm, setVoidConfirm] = useState('');
  const [refundOpen, setRefundOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ key: string; label: string; amount: number }[]>([]);

  useEffect(() => {
    if (!payment) return;
    setDraft(
      payment.allocations.map((allocation) => ({
        key: allocation.id,
        label: allocation.invoiceNumber ?? allocation.reservationReference ?? '—',
        amount: allocation.amount,
      })),
    );
    setEditing(false);
  }, [payment?.id, payment?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: paymentKeys.all }),
      queryClient.invalidateQueries({ queryKey: billingKeys.all }),
      queryClient.invalidateQueries({ queryKey: cashKeys.all }),
    ]);
  };

  const saveAllocations = useMutation({
    mutationFn: () => {
      if (!payment) throw new Error('missing');
      return updateAllocations(
        payment.id,
        draft
          .filter((line) => line.amount > 0)
          .map((line, index) => {
            const source = payment.allocations[index];
            return {
              ...(source?.invoiceId ? { invoiceId: source.invoiceId } : {}),
              ...(source?.reservationId ? { reservationId: source.reservationId } : {}),
              amount: line.amount,
            };
          }),
        payment.version,
      );
    },
    onSuccess: async () => {
      await invalidate();
      setEditing(false);
      toast.success(t('payments:toasts.allocationsUpdated'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const cancel = useMutation({
    mutationFn: () => {
      if (!payment) throw new Error('missing');
      return voidPayment(payment.id, voidReason.trim(), payment.version);
    },
    onSuccess: async () => {
      await invalidate();
      setVoidOpen(false);
      setVoidReason('');
      setVoidConfirm('');
      toast.success(t('payments:toasts.voided'));
    },
  });

  const receipt = useMutation({
    mutationFn: () => generateReceipt(paymentId),
    onSuccess: async (created) => {
      await invalidate();
      toast.success(t('payments:toasts.receiptGenerated', { number: created.number }));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  // « Télécharger » émet le reçu s'il ne l'était pas encore (l'émission est
  // idempotente côté serveur : aucun second numéro n'est consommé).
  const openPdf = useMutation({
    mutationFn: () => issueThenDownloadReceipt(paymentId, payment?.reference ?? null),
    onSuccess: (file) => openDownloadedFile(file),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={80} />
          <Skeleton height={340} />
        </div>
      </Container>
    );
  }

  if (query.isError || !payment) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const actions = paymentActions(payment, session.can);
  const badge = paymentBadge(payment);
  const draftTotal = draft.reduce((sum, line) => sum + line.amount, 0);
  const draftValid = draftTotal <= payment.amount;
  const auditRows = auditQuery.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Container>
      <PageHeader
        title={payment.reference}
        subtitle={t('payments:detail.subtitle', {
          customer: payment.customerName ?? t('payments:list.noCustomer'),
          date: formatDateTime(payment.paidAt, i18n.language),
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('payments:list.title'),
            link: <Link to="/billing/payments">{t('payments:list.title')}</Link>,
          },
          { label: payment.reference },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={badge.tone} size="lg" dot>
              {t(`payments:${badge.key}`)}
            </Badge>
            <Badge tone={methodTone(payment.method, payment.provider)} size="lg">
              {payment.provider
                ? t(`payments:provider.${payment.provider}`)
                : t(`payments:method.${payment.method}`)}
            </Badge>
          </div>
        }
      />

      <Card className="mb-4">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span className="strong display t-16">{formatAmount(payment.amount, i18n.language)}</span>
          <span className="spacer" />
          {actions.receipt ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                loading={receipt.isPending}
                onClick={() => receipt.mutate()}
              >
                <ReceiptIcon className="ico ico-14" aria-hidden="true" />
                {t('payments:actions.generateReceipt')}
              </Button>
              {actions.downloadReceipt ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={openPdf.isPending}
                  onClick={() => openPdf.mutate()}
                >
                  <Download className="ico ico-14" aria-hidden="true" />
                  {t('payments:actions.downloadReceipt')}
                </Button>
              ) : null}
            </>
          ) : null}
          {actions.send ? (
            <Button variant="secondary" size="sm" onClick={() => setSendOpen(true)}>
              <Mail className="ico ico-14" aria-hidden="true" />
              {t('payments:actions.sendReceipt')}
            </Button>
          ) : null}
          {actions.refund ? (
            <Button variant="secondary" size="sm" onClick={() => setRefundOpen(true)}>
              <RotateCcw className="ico ico-14" aria-hidden="true" />
              {t('payments:actions.refund')}
            </Button>
          ) : null}
          {actions.void ? (
            <Button variant="danger" size="sm" onClick={() => setVoidOpen(true)}>
              <Ban className="ico ico-14" aria-hidden="true" />
              {t('payments:actions.void')}
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      {payment.reversal ? (
        <Notice
          tone="danger"
          className="mb-4"
          title={t('payments:detail.reversedTitle')}
          icon={<Ban className="ico ico-20 notice__ico" aria-hidden="true" />}
        >
          {t('payments:detail.reversedBody', {
            reason: payment.reversal.reason,
            author: payment.reversal.reversedByName ?? '—',
            date: formatDateTime(payment.reversal.reversedAt, i18n.language),
          })}
        </Notice>
      ) : null}

      {payment.unallocatedAmount > 0 ? (
        <Notice tone="warning" className="mb-4">
          {t('payments:detail.unallocatedNotice', {
            amount: formatAmount(payment.unallocatedAmount, i18n.language),
          })}
        </Notice>
      ) : null}

      <div className="grid grid--main-aside">
        <div className="stack">
          {/* ---------------------------- Affectations --------------------------- */}
          <Card>
            <CardHeader
              title={t('payments:detail.allocationsTitle')}
              icon={<Banknote className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
              actions={
                actions.allocate ? (
                  editing ? (
                    <div className="row" style={{ gap: 6 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(false);
                          setDraft(
                            payment.allocations.map((allocation) => ({
                              key: allocation.id,
                              label:
                                allocation.invoiceNumber ?? allocation.reservationReference ?? '—',
                              amount: allocation.amount,
                            })),
                          );
                        }}
                      >
                        {t('common:actions.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!draftValid}
                        loading={saveAllocations.isPending}
                        onClick={() => saveAllocations.mutate()}
                      >
                        <Save className="ico ico-14" aria-hidden="true" />
                        {t('common:actions.save')}
                      </Button>
                    </div>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                      {t('payments:actions.editAllocations')}
                    </Button>
                  )
                ) : null
              }
            />
            <CardBody density="flush">
              {payment.allocations.length === 0 ? (
                <div className="card__body">
                  <EmptyState
                    icon={<Banknote className="ico ico-24" aria-hidden="true" />}
                    title={t('payments:detail.allocationsEmptyTitle')}
                    description={t('payments:detail.allocationsEmptyBody')}
                  />
                </div>
              ) : (
                <TableWrap>
                  <Table compact>
                    <thead>
                      <tr>
                        <Th>{t('payments:detail.allocationDocument')}</Th>
                        <Th align="right">{t('payments:columns.amount')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {payment.allocations.map((allocation, index) => (
                        <Tr key={allocation.id}>
                          <Td className="t-13">
                            {allocation.invoiceId ? (
                              <Link
                                to="/billing/invoices/$invoiceId"
                                params={{ invoiceId: allocation.invoiceId }}
                              >
                                <span className="cell-id">{allocation.invoiceNumber ?? '—'}</span>
                              </Link>
                            ) : allocation.reservationId ? (
                              <Link
                                to="/reservations/$reservationId"
                                params={{ reservationId: allocation.reservationId }}
                              >
                                <span className="cell-id">
                                  {allocation.reservationReference ?? '—'}
                                </span>
                              </Link>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td align="right" numeric>
                            {editing ? (
                              <Input
                                inputMode="numeric"
                                suffix="XOF"
                                style={{ width: 160 }}
                                aria-label={t('payments:allocation.amountFor', {
                                  label:
                                    allocation.invoiceNumber ??
                                    allocation.reservationReference ??
                                    '—',
                                })}
                                value={String(draft[index]?.amount ?? allocation.amount)}
                                onChange={(event) =>
                                  setDraft((lines) =>
                                    lines.map((line, position) =>
                                      position === index
                                        ? { ...line, amount: parseAmount(event.target.value) }
                                        : line,
                                    ),
                                  )
                                }
                              />
                            ) : (
                              formatAmount(allocation.amount, i18n.language)
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
              {editing && !draftValid ? (
                <div className="card__body">
                  <Notice tone="danger" role="alert">
                    {t('errors:PAYMENT_ALLOCATION_EXCEEDS_AMOUNT')}
                  </Notice>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {/* ------------------------------- Audit ------------------------------- */}
          <Card>
            <CardHeader
              title={t('payments:detail.historyTitle')}
              icon={<History className="ico ico-16" aria-hidden="true" />}
              iconTone="blue"
              actions={<Badge tone="neutral">{t('payments:detail.appendOnly')}</Badge>}
            />
            <CardBody>
              <div className="timeline">
                <div className="tl-item">
                  <span className="tl-item__dot">
                    <Banknote className="ico" aria-hidden="true" />
                  </span>
                  <div className="tl-item__title">{t('payments:detail.eventRecorded')}</div>
                  <div className="tl-item__meta">
                    {formatDateTime(payment.createdAt, i18n.language)} ·{' '}
                    {payment.recordedByName ?? '—'}
                  </div>
                </div>
                {payment.reversal ? (
                  <div className="tl-item">
                    <span className="tl-item__dot">
                      <Ban className="ico" aria-hidden="true" />
                    </span>
                    <div className="tl-item__title">{t('payments:detail.eventVoided')}</div>
                    <div className="tl-item__meta">
                      {formatDateTime(payment.reversal.reversedAt, i18n.language)} ·{' '}
                      {payment.reversal.reversedByName ?? '—'} · {payment.reversal.reason}
                    </div>
                  </div>
                ) : null}
                {auditRows.map((event) => (
                  <div className="tl-item" key={event.id}>
                    <span className="tl-item__dot">
                      <History className="ico" aria-hidden="true" />
                    </span>
                    <div className="tl-item__title">{event.action}</div>
                    <div className="tl-item__meta">
                      {formatDateTime(event.occurredAt, i18n.language)} · {event.actorName ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ------------------------------- Colonne ------------------------------- */}
        <aside className="stack sticky-aside">
          <Card>
            <CardHeader
              title={t('payments:detail.summaryTitle')}
              icon={<Wallet className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody density="tight">
              <dl className="dl dl--lines t-13">
                <dt>{t('payments:columns.amount')}</dt>
                <dd>
                  <b>{formatAmount(payment.amount, i18n.language)}</b>
                </dd>
                <dt>{t('payments:detail.allocated')}</dt>
                <dd>{formatAmount(payment.amount - payment.unallocatedAmount, i18n.language)}</dd>
                <dt>{t('payments:detail.unallocated')}</dt>
                <dd>{formatAmount(payment.unallocatedAmount, i18n.language)}</dd>
                <dt>{t('payments:detail.refunded')}</dt>
                <dd>{formatAmount(payment.refundedAmount, i18n.language)}</dd>
                <dt>{t('payments:detail.refundable')}</dt>
                <dd>{formatAmount(refundableOf(payment), i18n.language)}</dd>
                <dt>{t('payments:columns.date')}</dt>
                <dd>{formatDateTime(payment.paidAt, i18n.language)}</dd>
                <dt>{t('payments:fields.reference')}</dt>
                <dd className="mono">{payment.transactionReference ?? '—'}</dd>
                {payment.chequeNumber ? (
                  <>
                    <dt>{t('payments:fields.chequeNumber')}</dt>
                    <dd className="mono">{payment.chequeNumber}</dd>
                  </>
                ) : null}
                {payment.bankName ? (
                  <>
                    <dt>{t('payments:fields.bankName')}</dt>
                    <dd>{payment.bankName}</dd>
                  </>
                ) : null}
                <dt>{t('payments:detail.recordedBy')}</dt>
                <dd>{payment.recordedByName ?? '—'}</dd>
                {payment.cashSessionId ? (
                  <>
                    <dt>{t('cash:title')}</dt>
                    <dd>
                      <Link
                        to="/billing/cash/$sessionId"
                        params={{ sessionId: payment.cashSessionId }}
                      >
                        {t('payments:detail.openCashSession')}
                      </Link>
                    </dd>
                  </>
                ) : null}
              </dl>
              {payment.notes ? <p className="t-13 mt-3 mb-0">{payment.notes}</p> : null}
              {payment.proofDocumentId ? (
                <p className="t-12 muted mt-3 mb-0">
                  <Paperclip className="ico ico-14" aria-hidden="true" />{' '}
                  {t('payments:detail.proofAttached')}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </aside>
      </div>

      {/* ------------------------------ Dialogues ------------------------------ */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent
          title={t('payments:void.title')}
          description={t('payments:void.description', { reference: payment.reference })}
          icon={<Ban className="ico ico-16" aria-hidden="true" />}
          iconTone="red"
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setVoidOpen(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="danger"
                loading={cancel.isPending}
                disabled={
                  voidReason.trim().length < 5 ||
                  voidConfirm.trim().toUpperCase() !== payment.reference.toUpperCase()
                }
                onClick={() => cancel.mutate()}
              >
                {t('payments:actions.void')}
              </Button>
            </>
          }
        >
          <div className="stack">
            <Notice tone="danger">{t('payments:void.warning')}</Notice>
            {cancel.isError ? (
              <Notice tone="danger" role="alert">
                {errorMessage(cancel.error, t)}
              </Notice>
            ) : null}
            <FormField
              label={t('payments:void.reason')}
              required
              requiredLabel={t('common:fields.required')}
              hint={t('payments:void.reasonHint')}
            >
              {(field) => (
                <Textarea
                  id={field.id}
                  rows={3}
                  value={voidReason}
                  onChange={(event) => setVoidReason(event.target.value)}
                />
              )}
            </FormField>
            {/* Confirmation forte : ressaisie de la référence du paiement. */}
            <FormField
              label={t('payments:void.confirmLabel', { reference: payment.reference })}
              required
              requiredLabel={t('common:fields.required')}
            >
              {(field) => (
                <Input
                  id={field.id}
                  value={voidConfirm}
                  onChange={(event) => setVoidConfirm(event.target.value)}
                  placeholder={payment.reference}
                />
              )}
            </FormField>
          </div>
        </DialogContent>
      </Dialog>

      <RefundDrawer open={refundOpen} onOpenChange={setRefundOpen} payment={payment} />

      <SendDocumentDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        kind="payments"
        documentId={payment.id}
        documentLabel={payment.reference}
        defaultRecipient={null}
      />
    </Container>
  );
}
